// Copyright (c) Docugami, Inc. All rights reserved.

import { IContextualMenuItem } from "@fluentui/react";
import { DragWrapper, ItemCardletStyles, SelectedCardletStyle } from "components/cards/CardStyles";
import { DndLabel, DndType } from "components/cards/DndTypes";
import { ItemCardlet } from "components/cards/ItemCardlet";
import { PropertyChunkSlotItem, SlotItemType } from "components/cards/ItemSlot";
import { ItemSlots } from "components/cards/ItemSlots";
import { userFontFamily } from "components/common/Fonts";
import { primaryTheme } from "components/shell/presentation/AppStyles";
import { Property } from "hooks/documentProperties";
import { useAppSelector } from "hooks/redux";
import { useEffect, useMemo, useRef } from "react";
import { useDrag } from "react-dnd";
import { selectLabelState } from "store/frontend/selectors";
import { selectSelectedLabel } from "store/reports/selectors";
import styled from "styled-components";

const CardLabel = styled.div`
    font-family: ${userFontFamily};
    font-size: 16px;
    font-weight: bold;
    line-height: 16px;
    margin-bottom: 4px;
    color: ${primaryTheme.palette.black};
    cursor: default;

    // Overflow ellipsis behavior
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

interface PropertyListProps {
    properties: Property[];
    labelId: string;
    onClick?: (p: Property) => void;
    onDelete?: (p: Property) => void;
    onEdit?: (p: Property) => void;
    isDraggable?: boolean;
    onDragStart?: () => void;
    onDragEnd?: () => void;
    commands?: IContextualMenuItem[];
}

export const PropertyList = (props: PropertyListProps): JSX.Element => {
    const { onDragEnd, properties } = props;

    const selectedLabel = useAppSelector(selectSelectedLabel);
    const label = useAppSelector(state => selectLabelState(state.reportsSlice.clusterId!)(state).data?.entities[props.labelId]);
    const isSelected = label?.label === selectedLabel;

    const rootRef = useRef<HTMLDivElement | null>(null);

    const [{ isDragging }, dragRef, dragVisualRef] = useDrag<DndLabel, any, { isDragging: boolean }>({
        type: DndType.Label,
        item: {
            type: DndType.Label,
            label: label?.label ?? ''
        },
        canDrag: () => !!props.isDraggable,
        collect: m => ({
            isDragging: m.isDragging()
        }),
        end: onDragEnd
    }, [properties, props.isDraggable]);

    useEffect(() => {
        if (isDragging) {
            props.onDragStart?.();
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isDragging])

    dragVisualRef(rootRef)

    const cardletStyles: ItemCardletStyles | undefined = useMemo(() => {
        if (isSelected) {
            return {
                root: [SelectedCardletStyle]
            }
        }
    }, [isSelected])

    const slots = properties.map(p => ({
        type: SlotItemType.PropertyChunk,
        id: p.id,
        onEdit: () => props.onEdit?.(p)
    }) as PropertyChunkSlotItem)

    const header = (
        <CardLabel>{label?.label}</CardLabel>
    )

    return (
        <DragWrapper isDragging={isDragging}>
            <ItemCardlet
                ref={rootRef}
                dragHandleRef={!!props.isDraggable ? dragRef : undefined}
                header={header}
                canRemove={false}
                styles={cardletStyles}
                overflowCommands={props.commands}>
                <ItemSlots
                    slots={slots}
                    onClick={slot => props.onClick?.(properties.find(x => x.id === slot.id)!)}
                    scrollOnSelection={isSelected}
                    onRemove={slot => props.onDelete?.(properties.find(x => x.id === slot.id)!)} />
            </ItemCardlet>
        </DragWrapper>
    )
}
